import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  ArrowRight,
  Target,
  Users,
  Filter,
  Calendar,
  Shield,
  Activity,
  Menu,
  X
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_270b8cc9-1693-407c-b15e-5414d2b83f2e/artifacts/qe9vta95_LOGO-FIRMA-PNG-1-branco-258x278-1%20%282%29.png";

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const goToBooking = () => {
    setMobileMenuOpen(false);
    navigate('/book');
  };

  const navItems = [
    { label: 'How It Works', id: 'method' },
    { label: 'Case Studies', id: 'social-proof' },
    { label: 'Results', id: 'offer' },
    { label: 'FAQs', id: 'faq' },
  ];

  const painPoints = [
    'You spent years building your clinical reputation — but referrals come in randomly, with no pattern and no control.',
    "You tried advertising before and got price-shoppers, no-shows, and patients who want to use insurance you don't accept.",
    'You reduce your fees to fill the calendar — but that just means more work for less margin.',
    'Your best appointment slots sit empty while competitors with worse outcomes are fully booked.'
  ];

  const methodSteps = [
    {
      icon: Target,
      step: '01',
      title: 'We Find Patients Ready to Pay',
      description: 'Precision paid ads on Google and Meta targeting patients in your area who are actively searching for your specialty and have the means to pay out-of-pocket.'
    },
    {
      icon: Users,
      step: '02',
      title: 'We Convert Clicks Into Consultations',
      description: 'A custom landing page built for your top 3 services captures intent and turns ad clicks into booked consultation requests within minutes.'
    },
    {
      icon: Filter,
      step: '03',
      title: 'We Qualify Before You Spend a Minute',
      description: 'Every lead goes through an automated pre-qualification sequence via SMS and email. By the time they reach your calendar, they match your ideal patient profile.'
    },
    {
      icon: Calendar,
      step: '04',
      title: 'Your Calendar Fills Itself',
      description: 'Patients self-schedule into your calendar through an automated booking system. You arrive to work. The appointments are already there.'
    }
  ];

  const caseStudies = [
    {
      number: '63',
      label: 'booked appointments in 30 days',
      quote: 'We went from chasing referrals to having a waitlist.',
      author: 'Dr. Lucas Guerreiro',
      practice: 'Guerreiro Dermatology'
    },
    {
      number: '87',
      label: 'booked appointments in 30 days',
      quote: 'Our schedule is full three weeks ahead.',
      author: 'Dr. James Chen',
      practice: 'Plastic Surgery'
    },
    {
      number: '112',
      label: 'booked appointments in 30 days',
      quote: "Best marketing decision I've made in 15 years of practice.",
      author: 'Dr. Beatriz Rodriguez',
      practice: 'Med Spa'
    }
  ];

  const offerIncludes = [
    'Custom paid ad campaigns on Google and Meta — built for your specialty and your market',
    'High-converting landing pages for your top 3 services',
    'Automated pre-qualification system: SMS + email filtering leads before they reach you',
    'Calendar automation — patients self-book directly into your scheduling system',
    'Dedicated growth strategist — senior operator, not a junior account manager',
    'Weekly performance report: cost per lead, cost per booked appointment, ROI',
    'Monthly strategy call to optimize and plan the next growth phase'
  ];

  const offerExcludes = [
    'No long-term contracts — month-to-month with 30-day notice',
    'No insurance-dependent patients — private-pay only',
    'No junior account managers — senior strategist on your account'
  ];

  const guaranteeMilestones = [
    'Week 2: First qualified leads arriving in your pipeline.',
    'Week 4: First booked patient appointments from the system.',
    'Day 90: A measurable, documented increase in monthly private-pay patient volume.'
  ];

  const technicalProof = [
    'Cost per lead — you know exactly what each inquiry costs',
    'Cost per booked appointment — you know your acquisition cost per patient',
    'Return on ad spend — every dollar tracked to a real appointment',
    "Weekly optimization — we cut what's not working, double down on what is"
  ];

  const faqs = [
    {
      question: 'How fast can I expect results?',
      answer: 'First qualified leads in 7–14 days after launch. First booked patient appointments typically in weeks 2–3. Consistent volume by day 60.'
    },
    {
      question: 'How much does it cost?',
      answer: 'We cover investment details on the strategy call — it varies by specialty, market size, and growth goals. Most practices start between $1,500–$3,500/month in management fees, plus their own media spend.'
    },
    {
      question: 'Do I need a big ad budget?',
      answer: "We recommend a minimum of $1,500–$3,000/month in media spend. Below that, the data volume is too low to optimize properly. We'll be upfront about it."
    },
    {
      question: 'Do you work with multiple clinics?',
      answer: "We only work with one practice per specialty per market — that's how we guarantee our focus and your competitive advantage."
    },
    {
      question: "What if I've tried ads before and got burned?",
      answer: 'Most practices that failed with ads had campaigns built by generalist agencies. We only work with medical practices — your ads are built around how medical patients actually search and decide.'
    },
    {
      question: 'What happens after the call?',
      answer: "We audit your market and specialty, show you the exact system we'd build for your practice, and give you a clear proposal. You decide if it's the right fit — no pressure."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Top availability bar */}
      <div className="bg-[#0A0A0A] py-2.5">
        <p className="text-center text-xs sm:text-sm text-white font-light px-4" data-testid="availability-bar">
          Limited availability: We only onboard 3 new practices per month — Applications open now
        </p>
      </div>

      {/* Header */}
      <header data-testid="header" className="section-light sticky top-0 z-40 border-b border-[#E5E7EB] bg-white/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center gap-3" data-testid="logo">
              <img
                src={LOGO_URL}
                alt="Firma Medical Growth"
                className="h-12 w-auto invert"
              />
              <span className="hidden sm:block text-sm font-medium text-[#0A0A0A]">
                Medical Growth Systems
              </span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-sm font-medium text-[#6B7280] hover:text-[#0A0A0A] transition-colors"
                  data-testid={`nav-${item.id}`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <Button
                onClick={goToBooking}
                className="btn-primary"
                data-testid="header-cta"
              >
                Book My Free Call
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Microbadge */}
        <div className="bg-[#0A0A0A] py-2">
          <p className="text-center text-xs text-white font-light" data-testid="exclusivity-badge">
            Only 1 clinic per specialty per market — no internal competition.
          </p>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu lg:hidden" data-testid="mobile-menu">
          <div className="flex justify-between items-center mb-8">
            <img src={LOGO_URL} alt="Firma" className="h-10 w-auto" />
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="relative z-[60] p-2"
              data-testid="mobile-menu-close"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className="mobile-menu-item block w-full text-left"
              data-testid={`mobile-nav-${item.id}`}
            >
              {item.label}
            </button>
          ))}
          <Button
            onClick={goToBooking}
            className="btn-primary-inverse w-full mt-8"
          >
            Book My Free Call
          </Button>
        </div>
      )}

      {/* Hero Section */}
      <section data-testid="hero-section" className="section-light py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex flex-wrap justify-center gap-2 mb-6" data-testid="hero-badges">
            <span className="badge badge-light">Exclusive Patient Leads</span>
            <span className="badge badge-light">Done-For-You System</span>
            <span className="badge badge-light">No Shared Leads</span>
            <span className="badge badge-light">HIPAA-Conscious Structure</span>
          </div>

          <p className="text-sm font-medium uppercase tracking-wider text-[#6B7280] mb-5" data-testid="hero-preheadline">
            Attention: Private Practice Owners Tired of Depending on Insurance Reimbursements
          </p>

          <h1 className="heading-xl mb-6" data-testid="hero-headline">
            Private Medical Practices Across the U.S. Are Adding 25–50 Booked Patient Appointments Every Month — Without Insurance Networks, Referrals, or Cold Outreach.
          </h1>

          <p className="body-lg text-[#6B7280] mb-8 max-w-3xl mx-auto" data-testid="hero-subheadline">
            The system behind the results is called the Patient Growth System — a done-for-you paid acquisition engine that fills your calendar with qualified private-pay patients, week after week, without you lifting a finger.
          </p>

          <div className="flex flex-col items-center">
            <Button
              onClick={goToBooking}
              className="btn-primary flex items-center gap-2"
              data-testid="hero-cta"
            >
              Book My Free Patient Growth Call
              <ArrowRight className="w-5 h-5" />
            </Button>
            <p className="text-sm text-[#6B7280] mt-4" data-testid="hero-subtext">
              Free 30-min session. No contracts. No obligation.
            </p>
          </div>

          {/* Immediate social proof */}
          <p className="text-sm text-[#6B7280] mt-10 pt-8 border-t border-[#E5E7EB] max-w-3xl mx-auto" data-testid="hero-social-proof">
            Trusted by 90+ medical practices across the U.S.&nbsp;&nbsp;|&nbsp;&nbsp;25,000+ patient appointments booked&nbsp;&nbsp;|&nbsp;&nbsp;Results in 30 days or we extend free
          </p>
        </div>
      </section>

      {/* Pain Identification Section */}
      <section data-testid="pain-section" className="section-dark noise-overlay py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg mb-6" data-testid="pain-headline">
            The Truth About Growing a Private Practice in 2026 — Nobody Wants to Hear.
          </h2>

          <p className="body-lg text-[#9CA3AF] mb-8" data-testid="pain-intro">
            If you run a private practice, you already know this feeling:
          </p>

          <div className="space-y-4 mb-12" data-testid="pain-points">
            {painPoints.map((point, index) => (
              <div key={index} className="checklist-item" data-testid={`pain-point-${index}`}>
                <CheckCircle className="w-5 h-5 text-white flex-shrink-0 mt-1" />
                <span className="body-md text-[#9CA3AF]">{point}</span>
              </div>
            ))}
          </div>

          <p className="heading-md text-white border-l-4 border-white pl-6 mb-8" data-testid="pain-impact">
            Every empty slot isn't just lost revenue. It's a patient you didn't help — and a competitor who did.
          </p>

          <p className="body-lg text-[#9CA3AF]" data-testid="pain-diagnosis">
            The problem isn't your clinical skills. The problem is your patient acquisition system — or the lack of one.
          </p>
        </div>
      </section>

      {/* Method/System Section */}
      <section id="method" data-testid="method-section" className="section-light py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-center mb-16" data-testid="method-headline">
            The Patient Growth System: 4 Steps That Fill Your Calendar — On Autopilot
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="method-steps">
            {methodSteps.map((step, index) => (
              <div key={index} className="step-card" data-testid={`method-step-${index}`}>
                <span className="text-6xl font-bold text-[#E5E7EB]">{step.step}</span>
                <step.icon className="w-8 h-8 mt-4 mb-4" />
                <h3 className="heading-sm mb-3">{step.title}</h3>
                <p className="body-sm text-[#6B7280]">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-16 p-8 bg-[#F9FAFB] border border-[#E5E7EB]" data-testid="method-result">
            <p className="heading-md">
              More appointments → Higher patient value → Predictable monthly revenue — every single month.
            </p>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section id="social-proof" data-testid="social-proof-section" className="section-dark noise-overlay py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-center mb-16" data-testid="social-proof-headline">
            90+ Medical Practices. One System. These Are Their Numbers.
          </h2>

          {/* Case Studies — number-first layout */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="case-studies">
            {caseStudies.map((study, index) => (
              <div key={index} className="case-card text-center" data-testid={`case-study-${index}`}>
                <p className="text-7xl font-bold text-white leading-none mb-2">{study.number}</p>
                <p className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-8">{study.label}</p>
                <p className="text-base italic text-white mb-5">"{study.quote}"</p>
                <p className="font-medium text-white">{study.author}</p>
                <p className="text-sm text-[#9CA3AF]">{study.practice}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Offer Section */}
      <section id="offer" data-testid="offer-section" className="section-light py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-center mb-16" data-testid="offer-headline">
            Here's Exactly What You Get With the Patient Growth System
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* What's Included */}
            <div className="offer-card" data-testid="offer-includes">
              <h3 className="heading-sm mb-6">What's Included</h3>
              <ul className="space-y-4">
                {offerIncludes.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#0A0A0A] flex-shrink-0 mt-0.5" />
                    <span className="body-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* What You Won't Get */}
            <div className="offer-card flex flex-col" data-testid="offer-excludes">
              <h3 className="heading-sm mb-6">What You Won't Deal With</h3>
              <ul className="space-y-4 mb-8">
                {offerExcludes.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <X className="w-5 h-5 text-[#0A0A0A] flex-shrink-0 mt-0.5" />
                    <span className="body-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-2">
                <Button
                  onClick={goToBooking}
                  className="btn-primary w-full"
                  data-testid="offer-cta"
                >
                  Book My Free Strategy Call
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section data-testid="guarantee-section" className="section-dark noise-overlay py-24 lg:py-32">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-16 h-16 mx-auto mb-8" />
          <h2 className="heading-lg mb-6" data-testid="guarantee-headline">
            Our 90-Day Patient Growth Guarantee
          </h2>
          <p className="body-lg mb-10" data-testid="guarantee-text">
            We are so confident in the Patient Growth System that we back every engagement with a clear commitment:
          </p>
          <div className="text-left max-w-lg mx-auto mb-10" data-testid="guarantee-milestones">
            <ul className="space-y-4">
              {guaranteeMilestones.map((milestone, index) => (
                <li key={index} className="flex items-start gap-3 text-[#E5E7EB]" data-testid={`guarantee-milestone-${index}`}>
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-white" />
                  <span className="body-md">{milestone}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="body-lg text-[#9CA3AF]" data-testid="guarantee-close">
            If we don't hit all three milestones, we keep working at no additional charge until we do. No fine print. No asterisks. That's the deal.
          </p>
        </div>
      </section>

      {/* Technical / Traceability Section */}
      <section data-testid="technical-section" className="section-light py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Activity className="w-12 h-12 mx-auto mb-6" />
          <h2 className="heading-lg mb-12" data-testid="technical-headline">
            Fully Trackable. Fully Measurable. Zero Guesswork.
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto text-left" data-testid="technical-checklist">
            {technicalProof.map((item, index) => (
              <div key={index} className="flex items-start gap-3 p-4 bg-[#F9FAFB] border border-[#E5E7EB]" data-testid={`technical-item-${index}`}>
                <CheckCircle className="w-5 h-5 text-[#0A0A0A] flex-shrink-0 mt-0.5" />
                <span className="text-sm font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" data-testid="faq-section" className="section-dark noise-overlay py-24 lg:py-32">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-center mb-16" data-testid="faq-headline">
            Frequently Asked Questions
          </h2>

          <Accordion type="single" collapsible className="space-y-2" data-testid="faq-accordion">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#1F2937]">
                <AccordionTrigger
                  className="text-left text-lg font-medium text-white hover:text-[#9CA3AF] py-6"
                  data-testid={`faq-trigger-${index}`}
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent
                  className="text-[#9CA3AF] pb-6 leading-relaxed"
                  data-testid={`faq-content-${index}`}
                >
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="cta" data-testid="cta-section" className="section-light py-24 lg:py-32">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="heading-lg mb-6" data-testid="cta-headline">
            Ready to Stop Guessing Where Your Next Patient Is Coming From?
          </h2>
          <p className="body-lg text-[#6B7280] mb-10">
            Book a free 30-minute strategy call. We'll audit your market, show you exactly what the Patient Growth System looks like for your specialty, and give you a clear picture of what's possible in your first 90 days.
          </p>

          <div className="flex flex-col items-center">
            <Button
              onClick={goToBooking}
              className="btn-primary flex items-center gap-2"
              data-testid="cta-button"
            >
              Book My Free Strategy Call — Limited Spots This Week
              <ArrowRight className="w-5 h-5" />
            </Button>
            <p className="text-sm text-[#6B7280] mt-4">
              We only take 3 new medical practices per week to ensure full attention.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer data-testid="footer" className="section-dark py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Firma" className="h-10 w-auto" />
              <span className="text-sm text-[#9CA3AF]">Medical Growth Systems</span>
            </div>

            <div className="flex items-center gap-6 text-sm text-[#9CA3AF]">
              <a href="#" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors" data-testid="footer-terms">Terms</a>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-[#1F2937] text-center">
            <p className="text-xs text-[#6B7280] mb-4" data-testid="footer-exclusivity">
              Patient Exclusivity Policy: We work with only one clinic per specialty per market.
            </p>
            <p className="text-xs text-[#6B7280]">
              © {new Date().getFullYear()} Firma Medical Growth Systems. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
